import { baseUrl } from "@/constants";
import { Head, usePage } from "@inertiajs/react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export default function ({ title }: { title?: string }): ReactNode {
    const {
        props: { setting, currentLang },
    }: any = usePage();
    const { t } = useTranslation();

    // Get current URL for canonical and og:url
    const currentUrl = typeof window !== "undefined" ? window.location.href : baseUrl;
    const pageTitle = title ? t(title) : setting.name;
    const pageDescription = setting.description || setting.name;

    // Structured Data (JSON-LD) for SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": setting.name,
        "description": pageDescription,
        "url": baseUrl,
        "logo": setting.thumb,
        "telephone": setting.mobile,
        "sameAs": [
            setting.facebook_url,
            setting.twitter_url,
            setting.instagram_url,
            setting.youtube_url,
        ].filter(Boolean),
        "address": {
            "@type": "PostalAddress",
            "addressCountry": setting.country || "KW",
        }
    };

    return (
        <Head>
            {/* Primary Meta Tags */}
            <title>{pageTitle}</title>
            <meta name="title" content={pageTitle} />
            <meta name="description" content={pageDescription} />
            <meta name="author" content={setting.name} />

            {/* Robots & Crawling */}
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta name="googlebot" content="index, follow" />

            {/* Canonical URL */}
            <link rel="canonical" href={currentUrl} />

            {/* Language Alternates */}
            <link rel="alternate" hrefLang="ar" href={`${baseUrl}/ar`} />
            <link rel="alternate" hrefLang="en" href={`${baseUrl}/en`} />
            <link rel="alternate" hrefLang="x-default" href={baseUrl} />

            {/* Open Graph / Facebook Meta Tags */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={currentUrl} />
            <meta property="og:site_name" content={setting.name} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:image" content={setting.thumb} />
            <meta property="og:image:alt" content={setting.name} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:locale" content={currentLang === "ar" ? "ar_AR" : "en_US"} />
            <meta property="og:locale:alternate" content={currentLang === "ar" ? "en_US" : "ar_AR"} />

            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={currentUrl} />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content={setting.thumb} />
            <meta name="twitter:image:alt" content={setting.name} />

            {/* Mobile & Contact Meta Tags */}
            <meta name="mobile" content={setting.mobile} />
            <meta name="whatsapp" content={setting.whatsapp} />
            <meta name="telephone" content={setting.mobile} />

            {/* PWA & Mobile App Tags */}
            <meta name="mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="default" />
            <meta name="apple-mobile-web-app-title" content={setting.name} />
            <meta name="application-name" content={setting.name} />
            <meta name="msapplication-TileColor" content="#ffffff" />
            <meta name="msapplication-TileImage" content="/images/icons/fav/ms-icon-144x144.png" />
            <meta name="theme-color" content="#ffffff" />

            {/* Favicon & Icons */}
            <link rel="icon" type="image/png" sizes="16x16" href="/images/icons/fav/favicon-16x16.png" />
            <link rel="icon" type="image/png" sizes="32x32" href="/images/icons/fav/favicon-32x32.png" />
            <link rel="icon" type="image/png" sizes="96x96" href="/images/icons/fav/favicon-96x96.png" />
            <link rel="icon" type="image/png" sizes="192x192" href="/images/icons/fav/android-icon-192x192.png" />

            {/* Apple Touch Icons */}
            <link rel="apple-touch-icon" sizes="57x57" href="/images/icons/fav/apple-icon-57x57.png" />
            <link rel="apple-touch-icon" sizes="60x60" href="/images/icons/fav/apple-icon-60x60.png" />
            <link rel="apple-touch-icon" sizes="72x72" href="/images/icons/fav/apple-icon-72x72.png" />
            <link rel="apple-touch-icon" sizes="76x76" href="/images/icons/fav/apple-icon-76x76.png" />
            <link rel="apple-touch-icon" sizes="114x114" href="/images/icons/fav/apple-icon-114x114.png" />
            <link rel="apple-touch-icon" sizes="120x120" href="/images/icons/fav/apple-icon-120x120.png" />
            <link rel="apple-touch-icon" sizes="144x144" href="/images/icons/fav/apple-icon-144x144.png" />
            <link rel="apple-touch-icon" sizes="152x152" href="/images/icons/fav/apple-icon-152x152.png" />
            <link rel="apple-touch-icon" sizes="180x180" href="/images/icons/fav/apple-icon-180x180.png" />

            {/* Dynamic Icon from Settings */}
            <link rel="icon" href={setting.thumb} type="image/png" sizes="any" />
            <link rel="apple-touch-icon" href={setting.thumb} type="image/png" sizes="any" />

            {/* PWA Manifest */}
            <link rel="manifest" href="/manifest.json" />

            {/* Structured Data (JSON-LD) */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>
        </Head>
    );
}
